/**
 * "Add item + suppliers" — the two-step manual add (prototype `openAddItem`):
 *   1. pick the catalogue item
 *   2. set qty + UOM and tick every supplier to send it to
 *
 * Picking suppliers for a contract-priced item is an explicit decision to source it
 * competitively instead; the server flips the line's route to `rfq` on that basis.
 */

import { useState } from 'react'
import { Modal, SelectAllToggle } from '../../components/Modal'
import { Button } from '../../components/ui'
import { Money } from '../../components/Money'
import { CatalogPickerModal } from './CatalogPickerModal'
import { existingLineForItem, uomOptions } from '../../lib/rfq'
import type { EnquiryItem, EnquiryType, Item, NewEnquiryItem } from '../../lib/types'
import { nextSerial } from '../../lib/uomChoice'
import { renumberedFrom } from '../../lib/insertLine'

export function AddItemModal({
  enquiryType,
  lines = [],
  onAdd,
  onClose,
  busy,
}: {
  enquiryType: EnquiryType
  /** The enquiry's lines as they stand — for the line number the new one takes (2026-09-22), and
   *  for saying when the picked item is already on one (2026-10-16). */
  lines?: readonly Pick<EnquiryItem, 'cust_sno' | 'item_code'>[]
  onAdd: (line: NewEnquiryItem) => void
  onClose: () => void
  busy: boolean
}) {
  const [item, setItem] = useState<Item | null>(null)
  const [qty, setQty] = useState('1')
  // Akhil, 2026-09-22 (ENQ-2026-00289): the reader missed the customer's line 3, and the only
  // place a new line could go was the end. The number is the user's; lines from it move down.
  const [at, setAt] = useState(() => nextSerial(lines))
  const moving = renumberedFrom(lines.map((l) => ({ name: '', enquiry_row_no: l.cust_sno })), at)
  const [uom, setUom] = useState('')
  const [suppliers, setSuppliers] = useState<string[]>([])

  // Step 1 — the same catalogue picker the match flow uses.
  if (!item) {
    return (
      <CatalogPickerModal
        title="Add item to RFQ — 1. pick item"
        enquiryType={enquiryType}
        onPick={(picked) => {
          setItem(picked)
          setUom(picked.base_uom)
        }}
        onClose={onClose}
      />
    )
  }

  /*
    Already on the enquiry? Said, not refused (Akhil, 2026-10-16). A second line of one item is a
    variant — another colour, size or spec — priced and quoted on its own. The refusal this
    replaced ("change its quantity there instead") assumed a duplicate was always a mistake.
  */
  const already = existingLineForItem(lines, item.code)

  const candidates = item.candidate_suppliers
  const toggle = (supplier: string) =>
    setSuppliers((current) =>
      current.includes(supplier) ? current.filter((s) => s !== supplier) : [...current, supplier],
    )

  return (
    <Modal
      title={`Add “${item.name}” — set qty & suppliers`}
      onClose={onClose}
      footer={
        <>
          <SelectAllToggle
            checked={candidates.length > 0 && candidates.every((c) => suppliers.includes(c))}
            onChange={(checked) => setSuppliers(checked ? [...candidates] : [])}
          />
          <span style={{ flex: 1 }} />
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => onAdd({ item_code: item.code, qty: Math.max(1, Number(qty) || 1), uom, suppliers, ...(at.trim() ? { at: at.trim() } : {}) })}
          >
            {already ? 'Add anyway — as a separate line' : 'Add to RFQ'}
          </Button>
        </>
      }
    >
      <div className="pad">
        {already ? (
          <div className="banner" style={{ marginBottom: 10 }}>
            ⚠ <span className="mono">{item.code}</span> is already on this enquiry as line <b>#{already.cust_sno}</b>. Adding it again
            makes a <b>separate line</b> — for a variant (another colour, size or spec), priced and quoted on its own. To change
            the quantity of the existing line, edit it instead.
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <b>{item.name}</b>{' '}
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>
              {item.code} · {item.group}
            </span>
          </div>
          <span className="linkish" onClick={() => setItem(null)}>
            change item
          </span>
        </div>

        <div className="row" style={{ gap: 10 }}>
          <label className="fld" style={{ flex: '0 0 110px', margin: 0 }}>
            <span>Line no.</span>
            <input
              value={at}
              onChange={(e) => setAt(e.target.value)}
              title={moving.length > 0 ? `Lines ${moving.join(', ')} move down one` : 'The customer’s own line number'}
            />
          </label>
          <label className="fld" style={{ flex: 1, margin: 0 }}>
            <span>Qty</span>
            <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} />
          </label>
          <label className="fld" style={{ flex: 1, margin: 0 }}>
            <span>UOM</span>
            {/* Only the master's UOMs — anything else is a 422 on write (contract §6).
                Same lib/rfq source as the row picker, so the two can't diverge. */}
            <select value={uom} onChange={(e) => setUom(e.target.value)}>
              {uomOptions(item).map((c) => (
                <option key={c.uom} value={c.uom}>
                  {c.uom}
                  {c.factor === 1 ? ' (base)' : ` = ${c.factor} ${item.base_uom}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        {moving.length > 0 ? (
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
            Goes in as line {at.trim()}; {moving.length === 1 ? `line ${moving[0]} becomes ${Number(moving[0]) + 1}` : `lines ${moving[0]}–${moving[moving.length - 1]} move down one`}.
          </div>
        ) : null}

        {item.contracts.length && suppliers.length ? (
          <div style={{ fontSize: 11.5, color: 'var(--violet)', marginTop: 8 }}>
            This item has a contract rate. Picking suppliers sends it to RFQ instead.
          </div>
        ) : null}

        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', margin: '12px 0 0' }}>
          2. Send RFQ to (tick as many as you like):
        </div>
      </div>

      <div className="mb">
        {candidates.length === 0 ? (
          <div className="pad" style={{ color: 'var(--muted)' }}>
            No candidate suppliers on this item.
          </div>
        ) : null}
        {candidates.map((supplier) => {
          const contract = item.contracts.find((c) => c.supplier === supplier)
          return (
            <label key={supplier} className="supcheck">
              <input type="checkbox" checked={suppliers.includes(supplier)} onChange={() => toggle(supplier)} />
              <span>
                <b>{supplier}</b>
                <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                  {contract ? (
                    <>
                      contract <Money value={contract.rate} /> ·{' '}
                    </>
                  ) : null}
                  {/* Both figures the API gives. `on_shelf` is optional — unknown stock reads
                      "—", never 0, which would claim the item is out of stock. */}
                  stock {item.on_shelf ?? item.on_hand ?? '—'} {item.base_uom}
                  {item.reserved ? ` (${item.reserved} reserved)` : ''}
                </div>
              </span>
            </label>
          )
        })}
      </div>
    </Modal>
  )
}
